import React, { useCallback, useContext, useEffect, useState } from "react";
import { ReactComponent as UserIcon } from "../../../../assets/icons/fi-user.svg";
import { ReactComponent as BagIcon } from "../../../../assets/icons/bag-2.svg";
import { ReactComponent as CartIcon } from "../../../../assets/icons/cart.svg";
import { ReactComponent as TicketIcon } from "../../../../assets/icons/ticket.svg";
import { ReactComponent as TrashCanIcons } from "../../../../assets/icons/trash-can.svg";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import { ReactComponent as SignDocumentIcon } from "../../../../assets/icons/sign-document.svg";

import AddCustomerPopup from "./AddCustomerPopup";
import TabGeneralInfo from "./ViewDetailTabs/TabGeneralInfo";
import TabAccountInfo from "./ViewDetailTabs/TabAccountInfo";
import TabServiceInfo from "./ViewDetailTabs/TabServiceInfo";
import TabTicketInfo from "./ViewDetailTabs/TabTicketInfo";
import TabContractInfo from "./ViewDetailTabs/TabContractInfo";
import TabReportInfo from "./ViewDetailTabs/TabReportInfo";
import { ReactComponent as ReportIcon } from "../../../../assets/icons/report.svg";
import Modal from "../../components/Modal";
import { AuthContext } from "../../../../contexts/AuthContext";
import { customerService } from "../../../../services/api/customerService";
import { formatDate } from "../../../../utils/DateFormat";
import { truncateText } from "../../../../utils/TruncateText";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const CustomerInfo = () => {
  const { id } = useParams();
  const { token, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const tab = searchParams.get("tab") || "service";

  const changeTab = (newTab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", newTab);
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setShowDeleteConfirm(false);
      await customerService.delete(id, token);
      toast.success("Xóa khách hàng thành công!");
      navigate("/quan-ly-khach-hang");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể kết nối tới server.",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await customerService.get_by_id(id, token);
      setData(res.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Không thể kết nối tới server.",
      );
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchCustomers();
    if (user?.role === "SuperAdmin") setCanDelete(true);
  }, [fetchCustomers, user?.role]);

  const ActionButtons = () => (
    <div className="mt-6 flex items-end justify-end space-x-4">
      <button
        onClick={handleDeleteClick}
        className={`border-red-500 text-red-500 border rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-red-500 hover:text-white ${canDelete ? "" : "hidden"
          }`}
      >
        <TrashCanIcons /> Xoá khách hàng
      </button>
      <button
        onClick={() => setPopupVisible(true)}
        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
      >
        <EditIcon /> Chỉnh sửa
      </button>
    </div>
  );

  const renderTabContent = () => {
    switch (tab) {
      case "general":
        return (
          <>
            <TabGeneralInfo data={data} />
            <ActionButtons />
          </>
        );
      case "account":
        return (
          <>
            <TabAccountInfo data={data} />
            <ActionButtons />
          </>
        );
      case "service":
        return <TabServiceInfo />;
      case "ticket":
        return <TabTicketInfo />;
      case "contract":
        return <TabContractInfo />;
      case "report":
        return <TabReportInfo />;
      default:
        return <TabServiceInfo />;
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6 h-full overflow-auto">
      <div className="flex justify-between items-center gap-3 mb-4">
        <div>
          <p className="text-3xl font-bold text-gray-900">
            Thông tin chi tiết khách hàng
          </p>
        </div>
        <div className="flex justify-center items-center gap-4">
          <button
            onClick={handleDeleteClick}
            className={`border border-red-500 text-red-500 rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-red-500 hover:text-white transition-colors ${canDelete ? "" : "hidden"
              }`}
          >
            <TrashCanIcons className="w-4 h-4" /> Xoá khách hàng
          </button>
          <button
            onClick={() => setPopupVisible(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600 transition-colors"
          >
            <EditIcon className="w-4 h-4" /> Chỉnh sửa
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center border-b pb-4 gap-6">
        <div className="flex justify-start items-center space-x-4 w-full md:w-1/3 max-w-[300px]">
          <img
            src={data.avatar || data.avatarUrl || "/images/avatar-default.png"}
            alt="Avatar"
            className="w-[94px] h-[94px] rounded-lg object-cover object-center"
          />
          <div className="flex flex-col gap-2 justify-center">
            <h3 className="text-xl font-semibold whitespace-nowrap">
              {data?.fullName || data?.username || "Tên khách hàng"}
            </h3>
            <div className="flex items-center text-gray-600 gap-2">
              <UserIcon />
              {data?.role?.name || "Không có vai trò"}
            </div>
            {/* <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Phòng ban:</span>
              {data.responsibleDepartmentId?.name ? (
                <span className="px-2 py-0.5 rounded-md text-xs bg-blue-100 text-blue-700">
                  {data.responsibleDepartmentId.name}
                </span>
              ) : (
                <span className="text-xs text-gray-400">--</span>
              )}
            </div> */}
            {/* <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">NV phụ trách:</span>
              {data.assignedStaff?.fullName ? (
                <span className="px-2 py-0.5 rounded-md text-xs bg-green-100 text-green-700">
                  {data.assignedStaff.fullName}
                </span>
              ) : (
                <span className="text-xs text-gray-400">--</span>
              )}
            </div> */}
            <div className="text-gray-600 flex items-start gap-2">
              <img src="/icons/mail.svg" alt="Mail" />
              <span className="truncate">
                {truncateText(data.email || "", 20)}
              </span>
            </div>
          </div>
        </div>

        <div className="border-l-2 border-gray-300 h-[139px] mx-4 hidden md:block"></div>
        <div className="w-full md:w-1/3 text-sm flex flex-col gap-2">
          <p className="text-gray-500 uppercase font-sf-medium">
            Thông tin chung
          </p>
          <div className="grid grid-cols-2 gap-2 w-full">
            <label>Người đại diện</label>
            <h2 className="text-right">{data.fullName || "--"}</h2>
            <label>Email</label>
            <h2 className="text-right">{data.email || "--"}</h2>{" "}
            <label>Phòng ban phụ trách</label>{" "}
            <h2 className="text-right">
              {data.responsibleDepartmentId?.name || "--"}{" "}
            </h2>
            <label>Nhân viên phụ trách</label>
            <h2 className="text-right">
              {data.assignedStaff?.fullName || "--"}
            </h2>
            <label>SĐT</label>
            <h2 className="text-right">{data.phone || "--"}</h2>
            <label>Địa chỉ</label>
            <h2 className="text-right">
              {data.address?.detail || "--"}, {data.address?.district || "--"},{" "}
              {data.address?.city || "--"}
            </h2>
            <label>Giám sát viên</label>
            <h2 className="text-right">
              {data.supervisors?.map((s) => s.fullName || s.label || s.name).join(", ") || "--"}
            </h2>
          </div>
        </div>

        <div className="border-l-2 border-gray-300 h-[139px] mx-4 hidden md:block"></div>
        <div className="w-full md:w-1/4 text-sm flex flex-col gap-2">
          <p className="text-gray-500 uppercase font-sf-medium">Dữ liệu</p>
          <div className="grid grid-cols-2 gap-2 w-full">
            <label>Tạo bởi</label>
            <h2 className="text-right">{data.createBy || "--"}</h2>
            <label>Ngày tạo</label>
            <h2 className="text-right">{formatDate(data.createdDate)}</h2>
            <label>Cập nhật bởi</label>
            <h2 className="text-right">{data.modifiedBy || "--"}</h2>
            <label>Ngày cập nhật</label>
            <h2 className="text-right">{formatDate(data.updateDate)}</h2>
          </div>
        </div>
      </div>

      <div className="border-b w-full my-4">
        <div className="flex gap-4 text-sm justify-start flex-wrap">
          <button
            onClick={() => changeTab("service")}
            className={`flex py-3 gap-2 items-center px-2 justify-center text-center ${tab === "service"
              ? "border-b-2 border-blue-500 font-bold text-blue-500"
              : "text-gray-500"
              }`}
          >
            <CartIcon /> Danh sách hồ sơ
          </button>
          <button
            onClick={() => changeTab("ticket")}
            className={`flex py-3 gap-2 items-center px-2 justify-center text-center ${tab === "ticket"
              ? "border-b-2 border-blue-500 font-bold text-blue-500"
              : "text-gray-500"
              }`}
          >
            <TicketIcon /> Danh sách yêu cầu
          </button>
          <button
            onClick={() => changeTab("contract")}
            className={`flex py-3 gap-2 items-center px-2 justify-center text-center ${tab === "contract"
              ? "border-b-2 border-blue-500 font-bold text-blue-500"
              : "text-gray-500"
              }`}
          >
            <SignDocumentIcon /> Hợp đồng, chứng từ
          </button>
          <button
            onClick={() => changeTab("general")}
            className={`flex py-3 gap-2 items-center px-2 justify-center text-center ${tab === "general"
              ? "border-b-2 border-blue-500 font-bold text-blue-500"
              : "text-gray-500"
              }`}
          >
            <UserIcon /> Thông tin chung
          </button>
          <button
            onClick={() => changeTab("account")}
            className={`flex py-3 gap-2 items-center px-2 justify-center text-center ${tab === "account"
              ? "border-b-2 border-blue-500 font-bold text-blue-500"
              : "text-gray-500"
              }`}
          >
            <BagIcon /> Thông tin tài khoản
          </button>
          <button
            onClick={() => changeTab("report")}
            className={`flex py-3 gap-2 items-center px-2 justify-center text-center ${tab === "report"
              ? "border-b-2 border-blue-500 font-bold text-blue-500"
              : "text-gray-500"
              }`}
          >
            <ReportIcon /> Báo cáo thống kê
          </button>
        </div>
      </div>

      {renderTabContent()}

      {isPopupVisible && (
        <AddCustomerPopup
          isVisible={isPopupVisible}
          mode="edit"
          data={data}
          onClose={() => setPopupVisible(false)}
          onSubmit={fetchCustomers}
        />
      )}

      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Xác nhận xóa khách hàng"
        maxWidth="450px"
        primaryButtonText="Xóa"
        primaryButtonVariant="danger"
        primaryButtonOnClick={handleDeleteConfirm}
        cancelButtonText="Hủy"
      >
        <div className="px-6 py-4">
          <p className="text-gray-700 mb-3">
            Bạn có chắc chắn muốn xóa khách hàng{" "}
            <strong>{data.companyName || data.fullName || "này"}</strong> không?
          </p>
          <p className="text-sm text-red-600 font-medium">
            ⚠️ Hành động này không thể hoàn tác!
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Tất cả dữ liệu liên quan đến khách hàng này sẽ bị xóa vĩnh viễn.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default CustomerInfo;
